import { useEffect } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '@/lib/api'
import { useSSE } from '@/contexts/SSEContext'
import type { LiveSnapshotResponse } from '@/types/liveSnapshot'

/**
 * Snapshot temps réel de l'activité company (vue Live manager).
 *
 * Stratégie : 1 fetch initial via TanStack Query + invalidation déclenchée
 * par les events SSE `call` (déjà publiés par le backend). Pas de polling.
 *
 * Quand un CallLog est créé/mis à jour côté back, on_call_updated publie sur
 * Redis → le SSEContext propage l'event → on invalide la query → refetch léger
 * (~120ms back). C'est suffisant pour un radar live.
 */
export function useLiveSnapshot(companyId: number | undefined) {
  const queryClient = useQueryClient()
  const sse = useSSE()
  const key = ['live-snapshot', companyId] as const

  const query = useQuery({
    queryKey: key,
    queryFn: async () => {
      const { data } = await api.get<LiveSnapshotResponse>(
        `/api/companies/${companyId}/live-snapshot/`,
      )
      return data
    },
    enabled: Boolean(companyId),
    staleTime: 0,
    refetchOnWindowFocus: true,
  })

  useEffect(() => {
    if (!companyId) return
    // Tout event call (any status change) ré-invalide le snapshot.
    const offCall = sse.onCallUpdate(() => {
      void queryClient.invalidateQueries({ queryKey: ['live-snapshot', companyId] })
    })
    return () => offCall()
  }, [companyId, queryClient, sse])

  return query
}
